import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {AppAuthService} from '@app/auth';
import {ApiService} from '../../@core/utils/api.service';
import {SessionService} from '../../@core/utils/session.service';
import {AppThemeService} from '../prime/app-theme.service';
import {buildSeasonalPalette, loaderInk, parseHexColor, SeasonalPalette, SEASON_SURFACE_STEPS} from './seasonal-palette';

export const SEASON_PREVIEW_KEY = 'groin_theme_preview';

export interface SeasonalTheme {
	id: number;
	name: string;
	backgroundLight: string;
	tintLight: string;
	backgroundDark: string;
	tintDark: string;
	loaderLight: string;
	loaderDark: string;
	mark: string;
	animated: boolean;
}

export interface SeasonalChrome {
	preview: SeasonalTheme | null;
	mark: string | null;
	animated: boolean;
}

const PAINT_VARS = [
	'--app-page-bg',
	'--app-shell-bg',
	'--app-sidebar-bg',
	'--app-brand-soft',
	'--app-focus-ring',
	'--app-season-tint',
	...SEASON_SURFACE_STEPS.map((step) => `--p-surface-${step}`),
];

@Injectable({providedIn: 'root'})
export class SeasonalThemeService implements OnDestroy {
	readonly chrome$ = new BehaviorSubject<SeasonalChrome>({preview: null, mark: null, animated: false});
	private activeTheme: SeasonalTheme | null = null;
	private previewTheme: SeasonalTheme | null = null;
	private previewStamp = 0;
	private authenticated = false;
	private readonly subscriptions = new Subscription();

	constructor(
		private authService: AppAuthService,
		private apiService: ApiService,
		private sessionService: SessionService,
		private themeService: AppThemeService,
	) {
		this.subscriptions.add(this.authService.onTokenChange().subscribe((token) => {
			if (!token.isValid()) {
				this.authenticated = false;
				this.clearStoredPreview();
				this.activeTheme = null;
				this.previewTheme = null;
				this.paint(null, null, false, null);
				return;
			}
			this.authenticated = true;
			this.refreshActive();
			this.syncPreview();
		}));
		this.subscriptions.add(this.sessionService.getPermissionChangeEmitter().subscribe(() => this.syncPreview()));
		this.subscriptions.add(this.themeService.onThemeChange().subscribe(() => this.paintCurrent()));
		document.addEventListener('visibilitychange', this.onVisible);
	}

	ngOnDestroy(): void {
		document.removeEventListener('visibilitychange', this.onVisible);
		this.subscriptions.unsubscribe();
	}

	preview(theme: SeasonalTheme): void {
		this.previewStamp += 1;
		this.previewTheme = theme;
		if (theme.id) {
			localStorage.setItem(SEASON_PREVIEW_KEY, String(theme.id));
		}
		this.paintCurrent();
	}

	clearPreview(): void {
		this.clearStoredPreview();
		this.previewTheme = null;
		this.paintCurrent();
	}

	refreshActive(): void {
		this.apiService.getActiveUiTheme().subscribe({
			next: (response) => {
				this.activeTheme = this.readTheme(response?.theme);
				this.paintCurrent();
			},
			error: () => {
				this.activeTheme = null;
				this.paintCurrent();
			},
		});
	}

	private readonly onVisible = () => {
		if (document.visibilityState === 'visible' && this.authenticated) {
			this.refreshActive();
		}
	};

	private syncPreview(): void {
		if (!this.sessionService.isDieu()) {
			this.previewTheme = null;
			this.paintCurrent();
			return;
		}
		const raw = localStorage.getItem(SEASON_PREVIEW_KEY);
		const id = raw ? Number(raw) : NaN;
		if (!raw || !Number.isFinite(id)) {
			this.previewTheme = null;
			this.paintCurrent();
			return;
		}
		const stamp = this.previewStamp;
		this.apiService.getUiThemes().subscribe({
			next: (themes) => {
				if (stamp !== this.previewStamp) {
					return;
				}
				if (!this.sessionService.isDieu()) {
					this.previewTheme = null;
					this.paintCurrent();
					return;
				}
				const found = (themes || []).find((theme) => Number(theme.id) === id);
				if (!found) {
					this.clearStoredPreview();
					this.previewTheme = null;
				} else {
					this.previewTheme = this.readTheme(found);
				}
				this.paintCurrent();
			},
			error: () => {
				this.paintCurrent();
			},
		});
	}

	private paintCurrent(): void {
		const source = this.previewTheme ?? this.activeTheme;
		const palette = source
			? buildSeasonalPalette(
				source.backgroundLight,
				source.tintLight,
				source.backgroundDark,
				source.tintDark,
				this.themeService.isDark(),
			)
			: null;
		const mark = source && source.mark && source.mark !== 'none' ? source.mark : null;
		const animated = !!(source && source.animated && mark);
		const loaderBg = source
			? (this.themeService.isDark() ? source.loaderDark : source.loaderLight)
			: null;
		this.paint(palette, mark, animated, loaderBg);
	}

	private paint(palette: SeasonalPalette | null, mark: string | null, animated: boolean, loaderBg: string | null): void {
		const root = document.documentElement;
		for (const name of PAINT_VARS) {
			root.style.removeProperty(name);
		}
		root.classList.remove('app-season-dark-canvas');
		root.classList.toggle('app-season', !!palette);
		if (palette) {
			root.style.setProperty('--app-page-bg', palette.pageBg);
			root.style.setProperty('--app-shell-bg', palette.shellBg);
			root.style.setProperty('--app-sidebar-bg', palette.sidebarBg);
			root.style.setProperty('--app-brand-soft', palette.brandSoft);
			root.style.setProperty('--app-focus-ring', palette.focusRing);
			root.style.setProperty('--app-season-tint', palette.tint);
		}
		this.paintLoader(loaderBg);
		this.chrome$.next({
			preview: this.previewTheme,
			mark,
			animated,
		});
	}

	private readTheme(raw: any): SeasonalTheme | null {
		if (!raw || raw.id == null) {
			return null;
		}
		return {
			id: Number(raw.id),
			name: String(raw.name || ''),
			backgroundLight: String(raw.background_light || raw.background || ''),
			tintLight: String(raw.tint_light || raw.tint || ''),
			backgroundDark: String(raw.background_dark || raw.background || ''),
			tintDark: String(raw.tint_dark || raw.tint || ''),
			loaderLight: String(raw.loader_light || raw.background_light || raw.background || ''),
			loaderDark: String(raw.loader_dark || raw.background_dark || raw.background || ''),
			mark: String(raw.mark || 'none'),
			animated: raw.animated == null || Number(raw.animated) === 1 || raw.animated === true,
		};
	}

	private paintLoader(loaderBg: string | null): void {
		const spinner = document.getElementById('app-global-spinner');
		if (!spinner) {
			return;
		}
		if (!loaderBg || !parseHexColor(loaderBg)) {
			spinner.style.removeProperty('--groin-loader-bg');
			spinner.style.removeProperty('--groin-loader-ink');
			return;
		}
		spinner.style.setProperty('--groin-loader-bg', loaderBg);
		spinner.style.setProperty('--groin-loader-ink', loaderInk(loaderBg));
	}

	private clearStoredPreview(): void {
		localStorage.removeItem(SEASON_PREVIEW_KEY);
	}
}
